import { useState, useEffect, useCallback, useRef } from 'react';
import { Icons } from '../Icons';
import { useIsMobile } from '../../hooks/useIsMobile';

export interface Command {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  shortcut?: string;
  onExecute: () => void;
  category?: string;
  keywords?: string[];
}

interface CommandPaletteProps {
  commands: Command[];
}

export function CommandPalette({ commands }: CommandPaletteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Debug: Log when palette opens/closes
  useEffect(() => {
    console.log('🎯 Command Palette: isOpen changed to:', isOpen);
  }, [isOpen]);

  // Open with Cmd+K or Ctrl+K (disabled on mobile)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Disable command palette on mobile
      if (isMobile) {
        console.log('🎯 Command Palette: Disabled on mobile, ignoring keyboard shortcut');
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        console.log('🎯 Command Palette: Cmd/Ctrl+K pressed, opening palette');
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setSearch('');
        setSelectedIndex(0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isMobile]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Filter commands based on search
  const filteredCommands = commands.filter(cmd => {
    const searchLower = search.toLowerCase();
    const matchLabel = cmd.label.toLowerCase().includes(searchLower);
    const matchCategory = cmd.category?.toLowerCase().includes(searchLower);
    const matchKeywords = cmd.keywords?.some(k => k.toLowerCase().includes(searchLower));
    return matchLabel || matchCategory || matchKeywords;
  });

  // Group by category
  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    const category = cmd.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(cmd);
    return acc;
  }, {} as Record<string, Command[]>);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % filteredCommands.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + filteredCommands.length) % filteredCommands.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredCommands[selectedIndex]) {
        const cmd = filteredCommands[selectedIndex];
        console.log('🎯 Command Palette: Enter pressed for command:', cmd.id, cmd.label);
        console.log('🎯 Command Palette: Executing onExecute via Enter');
        cmd.onExecute();
        console.log('🎯 Command Palette: onExecute completed, closing palette');
        setIsOpen(false);
        setSearch('');
        setSelectedIndex(0);
      }
    }
  }, [filteredCommands, selectedIndex]);

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedElement = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  // Don't render on mobile
  if (isMobile || !isOpen) return null;

  return (
    <div
      className="command-palette-overlay"
      onClick={() => {
        setIsOpen(false);
        setSearch('');
        setSelectedIndex(0);
      }}
    >
      <div
        className="command-palette"
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="command-search">
          <Icons.Search size={18} className="command-search-icon" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Type a command or search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            className="command-input"
          />
          <kbd className="kbd">ESC</kbd>
        </div>

        {/* Results */}
        <div className="command-results" ref={listRef}>
          {Object.keys(groupedCommands).length === 0 ? (
            <div className="command-empty">
              <Icons.Search size={32} />
              <p>No commands found</p>
            </div>
          ) : (
            Object.entries(groupedCommands).map(([category, cmds]) => (
              <div key={category} className="command-category">
                <div className="command-category-label">{category}</div>
                {cmds.map((cmd, idx) => {
                  const globalIndex = filteredCommands.findIndex(c => c.id === cmd.id);
                  const Icon = cmd.icon;
                  return (
                    <div
                      key={cmd.id}
                      data-index={globalIndex}
                      className={`command-item ${globalIndex === selectedIndex ? 'selected' : ''}`}
                      onClick={() => {
                        console.log('🎯 Command Palette: Command clicked:', cmd.id, cmd.label);
                        console.log('🎯 Command Palette: Executing onExecute for:', cmd.id);
                        cmd.onExecute();
                        console.log('🎯 Command Palette: onExecute completed, closing palette');
                        setIsOpen(false);
                        setSearch('');
                        setSelectedIndex(0);
                      }}
                      onMouseEnter={() => setSelectedIndex(globalIndex)}
                    >
                      <Icon size={18} />
                      <span className="command-label">{cmd.label}</span>
                      {cmd.shortcut && <kbd className="kbd command-shortcut">{cmd.shortcut}</kbd>}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="command-footer">
          <div className="command-hint">
            <kbd className="kbd">↑↓</kbd> Navigate
          </div>
          <div className="command-hint">
            <kbd className="kbd">↵</kbd> Select
          </div>
          <div className="command-hint">
            <kbd className="kbd">ESC</kbd> Close
          </div>
        </div>
      </div>
    </div>
  );
}
